const CLIENT_IMAGE_PATH = "/client/img/";
const LUCIDE_ICON_PATH = "/client/ico/svg/lucide/";
const LUCIDE_SPRITE_PATH = "/node_modules/lucide-static/sprite.svg";
const LUCIDE_ICON_PREFIX = "lucide:";
const LUCIDE_ICON_NAME = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const kendoIconAdapterMarker = Symbol.for("Modelyn.KendoLucideIcons");

export function getIconPath(name) {
	if (!name) {
		return CLIENT_IMAGE_PATH + "null.svg";
	}
	if (name.startsWith(LUCIDE_ICON_PREFIX)) {
		return getLucideIconPath(name.substring(LUCIDE_ICON_PREFIX.length));
	}
	if (name.includes("/")) {
		return name;
	}
	return CLIENT_IMAGE_PATH + name + (name.includes(".") ? "" : ".svg");
}

export function getLucideIconPath(name) {
	return LUCIDE_ICON_PATH + name + (name.includes(".") ? "" : ".svg");
}

export function getImagePath(name) {
	if (name.includes("/")) {
		return name;
	}
	return CLIENT_IMAGE_PATH + "sample/" + name + (name.includes(".") ? "" : ".png");
}

export function getImageOrIconPath(imageName, iconName) {
	return imageName ? getImagePath(imageName) : getIconPath(iconName);
}

export function getWidgetIconPath(subType, externalType) {
	let iconName;
	if (!subType) {
		iconName = "control";
	}
	else if (subType === "external") {
		iconName = externalType || "html";
	}
	else {
		switch (subType) {
			case "htmlblock-inline":
				iconName = "htmlblock";
				break;
			case "inline-object":
			case "object":
				iconName = "control";
				break;
			default:
				iconName = subType;
				break;
		}
	}
	return CLIENT_IMAGE_PATH + iconName + ".svg";
}

function getLucideSpecifierName(icon) {
	if (typeof icon !== "string" || !icon.startsWith(LUCIDE_ICON_PREFIX)) {
		return null;
	}
	const name = icon.substring(LUCIDE_ICON_PREFIX.length);
	return LUCIDE_ICON_NAME.test(name) ? name : null;
}

function createKendoLucideIcon(name) {
	return {
		name: `lucide-${name}`,
		viewBox: "0 0 24 24",
		content: `<g fill="none" stroke="currentColor" stroke-width="2" `
			+ `stroke-linecap="round" stroke-linejoin="round">`
			+ `<use href="${LUCIDE_SPRITE_PATH}#${name}"></use></g>`
	};
}

function normalizeKendoIconOptions(value) {
	const directName = getLucideSpecifierName(value);
	if (directName) {
		return { icon: createKendoLucideIcon(directName) };
	}
	const optionName = getLucideSpecifierName(value?.icon);
	return optionName ? { ...value, icon: createKendoLucideIcon(optionName) } : value;
}

function wrapKendoIconRenderer(renderer) {
	return function (element, options) {
		if (arguments.length < 2) {
			return renderer.call(this, normalizeKendoIconOptions(element));
		}
		return renderer.call(this, element, normalizeKendoIconOptions(options));
	};
}

// Installs Modelyn's icon prefix on the shared Kendo renderer. Modelyn tries during
// initialization and after Kendo batches because an earlier core-only load has no renderer yet.
export function installKendoIconRenderer(kendoRuntime = globalThis.kendo) {
	if (!kendoRuntime || kendoRuntime[kendoIconAdapterMarker]) {
		return Boolean(kendoRuntime?.[kendoIconAdapterMarker]);
	}
	const htmlRenderer = kendoRuntime.html?.renderIcon;
	const uiRenderer = kendoRuntime.ui?.icon;
	if (typeof htmlRenderer !== "function" || typeof uiRenderer !== "function") {
		return false;
	}

	const wrappedHtmlRenderer = wrapKendoIconRenderer(htmlRenderer);
	kendoRuntime.html.renderIcon = wrappedHtmlRenderer;
	kendoRuntime.ui.icon = uiRenderer === htmlRenderer
		? wrappedHtmlRenderer
		: wrapKendoIconRenderer(uiRenderer);
	kendoRuntime[kendoIconAdapterMarker] = true;
	return true;
}
